import type AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import * as React from 'react';

export type CurrentFacilityContextValue = {
	/** `null` before resolution, and whenever a persisted identifier is no longer an assigned facility
	 * (§ retrait d'affectation) — never auto-reselected, choosing another one is always explicit. */
	readonly currentFacility: AccountFacilityView | null;
	/** The current organization's facilities, sorted by name. */
	readonly facilities: readonly AccountFacilityView[];
	/** `true` once the current-facility resolution effect has settled — a server-confirmed empty
	 * organization list, a persisted identifier reconciled against the current organization's
	 * facilities, or an organization change already reflected in `currentFacility`. Stays `false`
	 * while the underlying current-organization context is still resolving, since the facility list
	 * cannot be trusted before the organization is known. */
	readonly isResolved: boolean;
	/** Persists the choice, updates `currentFacility`, then navigates — to `redirectTo` when given,
	 * to the home (`/`) otherwise: never staying on a screen scoped to the facility just left. Assumes
	 * the facility belongs to the current organization — a cross-organization pick
	 * (`FacilitySelector`) calls `useCurrentOrganization().selectOrganization` first, in the same
	 * event. The optional `redirectTo` exists for callers (the synchronisation queue screen) that
	 * switch facility and land on a specific destination in one motion — issuing two separate
	 * navigations in sequence would leave one render in between where the screen is already mounted
	 * under the previous facility, its requests failing with a `404`. */
	readonly selectFacility: (facilityId: number, options?: { readonly redirectTo?: string }) => void;
};

const CurrentFacilityContext = React.createContext<CurrentFacilityContextValue | null>(null);

export default CurrentFacilityContext;
